import {
  StartMedicalStreamTranscriptionCommand,
  StartStreamTranscriptionCommand,
  TranscribeStreamingClient,
} from '@aws-sdk/client-transcribe-streaming'

export type SpecialtyType =
  | 'CARDIOLOGY'
  | 'NEUROLOGY'
  | 'ONCOLOGY'
  | 'PRIMARYCARE'
  | 'RADIOLOGY'
  | 'UROLOGY'

export interface TranscribeConfig {
  region: string
  credentials: {
    accessKeyId: string
    secretAccessKey: string
    sessionToken?: string
  }
}

export interface AudioStreamEvent {
  AudioEvent: {
    AudioChunk: Uint8Array
  }
}

class TranscribeService {
  private client: TranscribeStreamingClient | null = null
  private activeStream: any = null

  initialize(config: TranscribeConfig) {
    if (this.client) {
      this.destroy()
    }

    this.client = new TranscribeStreamingClient({
      region: config.region,
      credentials: {
        accessKeyId: config.credentials.accessKeyId,
        secretAccessKey: config.credentials.secretAccessKey,
        sessionToken: config.credentials.sessionToken,
      },
    })
  }

  async startStandardTranscription(
    audioStream: AsyncGenerator<AudioStreamEvent>
  ) {
    if (!this.client) {
      throw new Error('Transcribe client not initialized')
    }

    try {
      const command = new StartStreamTranscriptionCommand({
        LanguageCode: 'en-US',
        MediaSampleRateHertz: 16000,
        MediaEncoding: 'pcm',
        AudioStream: audioStream,
      })

      this.activeStream = await this.client.send(command)
      return this.activeStream
    } catch (error) {
      this.cleanup()
      throw error
    }
  }

  async startMedicalTranscription(
    audioStream: AsyncGenerator<AudioStreamEvent>,
    specialty: SpecialtyType = 'PRIMARYCARE'
  ) {
    if (!this.client) {
      throw new Error('Transcribe client not initialized')
    }

    try {
      const command = new StartMedicalStreamTranscriptionCommand({
        LanguageCode: 'en-US',
        MediaSampleRateHertz: 16000,
        MediaEncoding: 'pcm',
        AudioStream: audioStream,
        Specialty: specialty,
        Type: 'DICTATION',
        ShowSpeakerLabel: false,
        ContentIdentificationType: 'PHI',
      })

      this.activeStream = await this.client.send(command)
      return this.activeStream
    } catch (error) {
      this.cleanup()
      throw error
    }
  }

  private cleanup() {
    if (this.activeStream) {
      try {
        if (this.activeStream.TranscriptResultStream) {
          this.activeStream.TranscriptResultStream.destroy?.()
        }
        this.activeStream = null
      } catch (error) {
        console.error('Error cleaning up stream:', error)
      }
    }
  }

  destroy() {
    this.cleanup()
    if (this.client) {
      this.client.destroy()
      this.client = null
    }
  }
}

export const transcribeService = new TranscribeService()
